import styled from 'styled-components';

/**
 * Where a connection stands, which is what the pill on the brand says:
 *   live  - still connected while the problem is open: the conflict
 *   kept  - the older brand vGrubs left connected
 *   cut   - the newer one vGrubs disconnected
 *   past  - resolved by hand, nobody recorded which side went: no pill
 */
export type ConnectionTone = 'live' | 'kept' | 'cut' | 'past';

/** Below this a brand row stacks its pill and buttons under the name. */
const STACK = '600px';

/** The provider logo's disc. */
const LOGO = 44;

/**
 * One store on top, the brands it ended up on as full-width rows under it.
 *
 * Vertical on purpose: side by side, three columns left each brand about
 * 170px of a 560px modal, and real names ("Roštilj kod Pobratima") and UUID
 * store ids broke over three lines. Stacked, every name gets the full width,
 * and a store that collided with several brands simply adds rows.
 */
export const Tree = styled.div`
  margin-top: 12px;
`;

/** The store: logo, provider, its id at full width, POS Hub / KitchenHub. */
export const StoreHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

export const StoreLogo = styled.div`
  display: flex;
  flex: none;
  align-items: center;
  justify-content: center;
  width: ${LOGO}px;
  height: ${LOGO}px;
  border-radius: 50%;
  background: ${({ theme }) => theme.white};
  border: ${({ theme }) => theme.borders.border1};
  box-shadow: ${({ theme }) => theme.shadows.shadow2};

  img {
    width: 32px;
    height: 32px;
    border-radius: 50%;
  }
`;

export const StoreText = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  min-width: 0;
`;

export const StoreTitle = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 8px;
  font-size: 16px;
  line-height: 20px;
  font-weight: 700;
  color: ${({ theme }) => theme.darkGray};
`;

/** A store id can be a UUID; it breaks anywhere rather than widen the row. */
export const StoreId = styled.span`
  font-size: 13px;
  line-height: 18px;
  color: ${({ theme }) => theme.lightGray};
  word-break: break-all;
`;

/** POS Hub / KitchenHub, as a small grey tag beside the provider's name. */
export const SourceTag = styled.span`
  padding: 2px 8px;
  border-radius: 100px;
  font-size: 11px;
  line-height: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.darkGray};
  background: ${({ theme }) => theme.gray};
`;

/** One brand row, a little apart from the store and from each other. */
export const Branch = styled.div`
  padding-top: 12px;
`;

/** One brand. White on the modal's cream, like the Dashboard's cards. */
export const BrandNode = styled.div`
  display: flex;
  /* Stretch, so the right column is as tall as the text beside it and can
     keep its links on the bottom edge (see BrandSide). */
  align-items: stretch;
  justify-content: space-between;
  gap: 12px 16px;
  min-width: 0;
  padding: 14px 16px;
  background: ${({ theme }) => theme.white};
  border: ${({ theme }) => theme.borders.border1};
  border-radius: 16px;

  @media (max-width: ${STACK}) {
    flex-direction: column;
  }
`;

/** Role, name, location and who - the column that gets the width. */
export const BrandText = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

/**
 * The state pill over the two links, at the row's right edge: the pill level
 * with the role on top, the links level with the last line on the left
 * (usually "by ..."), however many lines the left side runs to. Centred, they
 * drifted against the text whenever a location or provider line was added.
 */
export const BrandSide = styled.div`
  display: flex;
  flex: none;
  flex-direction: column;
  align-items: flex-end;
  justify-content: space-between;
  gap: 10px;

  @media (max-width: ${STACK}) {
    flex-direction: row;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    width: 100%;
  }
`;

/** Names the brand's role. Small and grey: the brand under it is the point. */
export const Eyebrow = styled.span`
  font-size: 11px;
  line-height: 14px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${({ theme }) => theme.lightGray};
`;

export const BrandName = styled.span`
  font-size: 16px;
  line-height: 22px;
  font-weight: 700;
  color: ${({ theme }) => theme.darkGray};
  overflow-wrap: anywhere;
`;

export const Muted = styled.span`
  font-size: 13px;
  line-height: 18px;
  color: ${({ theme }) => theme.lightGray};
  overflow-wrap: anywhere;
`;

/**
 * "by" and the user's TextButton on one line. The button is 100% wide by
 * default; shrunk to its words so only the name is clickable.
 */
export const ByLine = styled.div`
  display: flex;
  /* Baseline, not centre: "by" is the app font and the TextButton is the
     browser's button font, and centring their boxes left "by" sitting low. */
  align-items: baseline;
  gap: 4px;
  font-size: 12px;
  line-height: 16px;
  color: ${({ theme }) => theme.lightGray};

  button {
    width: auto;
    /* Only as tall as the words: TextButton's 30px box left empty space
       under them, which read as extra padding at the card's bottom edge. */
    height: 16px;
    line-height: 16px;
    /* Sized here rather than with a TextButton prop: dev already has a
       shared $small with its own (13px) sizing, master has none yet, and
       this row should look the same on both. Class plus element, so it also
       beats TextButton's own 14px phone rule. */
    font-size: 12px;
    font-weight: 600;
    white-space: nowrap;
  }
`;

/**
 * View Location / View Brand, side by side - stacked they made the row too
 * tall. The app's TextButton, red and underlined like the other
 * text actions in the admin (Attach order, Hand back). It is 100% wide by default,
 * which would make the whole row one click target; shrink each to its words.
 */
export const BrandActions = styled.div`
  display: flex;
  gap: 16px;

  button {
    width: auto;
    /* Only as tall as the words: TextButton's 30px box left empty space
       under them, which read as extra padding at the card's bottom edge. */
    height: 16px;
    line-height: 16px;
    /* Sized here rather than with a TextButton prop: dev already has a
       shared $small with its own (13px) sizing, master has none yet, and
       this row should look the same on both. Class plus element, so it also
       beats TextButton's own 14px phone rule. */
    font-size: 12px;
    font-weight: 600;
    white-space: nowrap;
  }
`;
